import { i18n } from '@quadralize/i18n'
import { cn, parseIsoDate, toIsoDate } from '@quadralize/utils'
import { CloseCircle, Magnifier } from '@solar-icons/react'
import { useTranslation } from 'react-i18next'
import { Combobox } from '../../forms/combobox/combobox'
import { DatePicker } from '../../forms/date-picker/date-picker'
import { Input } from '../../forms/input/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../forms/select/select'
import { Button } from '../../ui/button/button'
import { clampIsoDate } from './data-table-date-filter.utils'
import type { DataTableFiltersProps } from './data-table-filters.types'
import { getActiveFilterCount } from './data-table-filters.types'

/**
 * Generic DataTable filters component for desktop view
 * Displays search input, select filters, and optional clear button in a horizontal layout
 */
export function DataTableFilters({
  className,
  search,
  filters = [],
  onClearFilters,
}: DataTableFiltersProps) {
  const { t } = useTranslation(['ui', 'common'], { i18n })
  if (!search && filters.length === 0) {
    return null
  }

  const activeFilterCount = getActiveFilterCount(filters)
  const hasActiveFilters = activeFilterCount > 0

  const getAllOptionLabel = (label: string) => {
    if (label === 'Status') return t('dataTable.filters.all', { ns: 'ui' })
    if (label === 'Esporte') return t('dataTable.filters.allSports', { ns: 'ui' })
    if (label === 'Categoria') return t('dataTable.filters.allCategories', { ns: 'ui' })
    if (label === 'Quadra') return 'Todas as quadras'
    if (label === 'Tipo') return 'Todos os tipos'
    return `Todos ${label}`
  }

  return (
    <div
      className={cn(
        'w-full flex flex-col items-start gap-3 md:flex-row md:flex-wrap md:items-center',
        className,
      )}
    >
      {/* Search Input */}
      {search && (
        <div className="relative w-full md:min-w-72 md:flex-1">
          <Input
            type="search"
            startIcon={<Magnifier />}
            placeholder={search.placeholder}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            clearable
            clearLabel={t('dataTable.filters.clearSearch', { ns: 'ui' })}
            className="pl-9 h-10"
          />
        </div>
      )}

      {/* Select Filters */}
      {filters.length > 0 && (
        <div className="flex w-full flex-col gap-3 sm:flex-row md:w-auto md:shrink-0">
          {filters.map((filter) => (
            <div key={filter.id} className={filter.width || 'w-full sm:w-45'}>
              {filter.type === 'date' ? (
                <DatePicker
                  date={parseIsoDate(filter.value)}
                  setDate={(date) => {
                    const nextValue = clampIsoDate(toIsoDate(date), filter.min, filter.max)
                    filter.onValueChange(nextValue)
                  }}
                  className="h-10 md:h-10"
                  placeholder={filter.placeholder}
                  minDate={filter.min ? (parseIsoDate(filter.min) ?? undefined) : undefined}
                  isDateDisabled={
                    filter.max
                      ? (date) => {
                          const maxDate = parseIsoDate(filter.max ?? '')
                          return !!maxDate && date > maxDate
                        }
                      : undefined
                  }
                />
              ) : filter.searchable ? (
                <Combobox
                  value={String(filter.value) === 'all' ? '' : String(filter.value)}
                  onValueChange={(value) => {
                    filter.onValueChange(value ? value : 'all')
                  }}
                  options={[
                    ...(filter.showAllOption !== false
                      ? [{ value: 'all', label: getAllOptionLabel(filter.label) }]
                      : []),
                    ...filter.options.map((option) => ({
                      value: String(option.value),
                      label: option.label,
                    })),
                  ]}
                  fetcher={
                    filter.fetchOptions
                      ? async (query) => {
                          const fetchedOptions = await filter.fetchOptions?.(query)
                          return [
                            ...(filter.showAllOption !== false
                              ? [{ value: 'all', label: getAllOptionLabel(filter.label) }]
                              : []),
                            ...(fetchedOptions ?? []).map((option) => ({
                              value: String(option.value),
                              label: option.label,
                            })),
                          ]
                        }
                      : undefined
                  }
                  placeholder={filter.placeholder}
                  searchPlaceholder={filter.searchPlaceholder ?? `Buscar ${filter.label}`}
                  emptyMessage={
                    filter.emptyMessage ?? t('dataTable.filters.noResults', { ns: 'ui' })
                  }
                  clearable
                  className="h-10 min-h-10 md:h-10 md:min-h-10"
                />
              ) : (
                <Select
                  value={(filter.value as string) || undefined}
                  onValueChange={filter.onValueChange}
                >
                  <SelectTrigger className="h-10">
                    <SelectValue placeholder={filter.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {filter.showAllOption !== false && (
                      <SelectItem value="all">{getAllOptionLabel(filter.label)}</SelectItem>
                    )}
                    {filter.options.map((option) => (
                      <SelectItem key={String(option.value)} value={String(option.value)}>
                        {option.icon ? (
                          <div className="flex items-center gap-2">
                            {option.icon}
                            <span className="truncate">{option.label}</span>
                          </div>
                        ) : (
                          option.label
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Clear Filters Button */}
      {(hasActiveFilters || !!search?.value) && onClearFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="h-10 md:h-10 text-text-secondary hover:text-text-primary shrink-0"
          startIcon={<CloseCircle className="h-4 w-4" />}
        >
          {t('actions.clearFilters', { ns: 'common' })}
        </Button>
      )}
    </div>
  )
}
